/**
 * Паритет стилей строки TreeTable со Swing: UniDataStyleConverter + IKey / IKeys.
 * Разделители как в Java: COLUMN_DELIMETR ";-;", KEY_VALUE_DELIMETR ";".
 */

export const SWING_COLUMN_BLOCK_DELIM = ';-;'
export const SWING_KEY_VALUE_DELIM = ';'

export type TreeTableColumnPresentation = {
  color?: string
  backgroundColor?: string
  fontWeight?: string
  fontStyle?: string
  padding?: string
  /** служебный флаг с бэка */
  ignoreReadOnly?: boolean
}

export type TreeTableRowSwingPresentation = {
  rowInlineStyle: Record<string, string>
  rowTitle: string
  iconFileName?: string
  perColumn: Record<string, TreeTableColumnPresentation>
}

function unwrapField(val: unknown): unknown {
  if (val !== null && typeof val === 'object' && 'value' in val && (val as { value: unknown }).value !== undefined) {
    return (val as { value: unknown }).value
  }
  return val
}

function asRecord(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {}
  return data as Record<string, unknown>
}

/** Читает поле/attr с корня, fields.*, attributes.* (setAttr в UniDataTableRow) и _rowData.* */
export function readSwingRowKey(row: Record<string, unknown>, key: string): unknown {
  const u = unwrapField

  const tryGet = (obj: Record<string, unknown> | undefined): unknown => {
    if (!obj || !Object.prototype.hasOwnProperty.call(obj, key)) return undefined
    return u(obj[key])
  }

  let v = tryGet(row)
  if (v !== undefined) return v

  const fields = row.fields as Record<string, unknown> | undefined
  v = tryGet(fields)
  if (v !== undefined) return v

  const attributes = row.attributes as Record<string, unknown> | undefined
  v = tryGet(attributes)
  if (v !== undefined) return v

  const rd = row._rowData as Record<string, unknown> | undefined
  if (rd) {
    v = tryGet(rd)
    if (v !== undefined) return v
    v = tryGet(rd.fields as Record<string, unknown> | undefined)
    if (v !== undefined) return v
    v = tryGet(rd.attributes as Record<string, unknown> | undefined)
    if (v !== undefined) return v
  }
  return undefined
}

export function readSwingRowString(row: Record<string, unknown>, key: string): string {
  const v = readSwingRowKey(row, key)
  if (v == null) return ''
  return String(v).trim()
}

/** Пары column;value по блокам ;-; (как StringUtils.splitByWholeSeparator). */
export function parseSwingKeyedComposite(raw: string): Array<{ column: string; value: string }> {
  const s = raw == null ? '' : String(raw).trim()
  if (!s) return []
  const blocks = s.split(SWING_COLUMN_BLOCK_DELIM)
  const out: Array<{ column: string; value: string }> = []
  for (const block of blocks) {
    const part = block.trim()
    if (!part) continue
    const d = part.indexOf(SWING_KEY_VALUE_DELIM)
    if (d <= 0) continue
    const column = part.slice(0, d).trim()
    const value = part.slice(d + SWING_KEY_VALUE_DELIM.length).trim()
    if (column) out.push({ column, value })
  }
  return out
}

function normalizeCssColor(raw: string): string {
  const s = raw.trim()
  if (!s) return ''

  const oxMatch = /^0[xX]([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(s)
  if (oxMatch) {
    const rgb = oxMatch[1]
    const alphaHex = oxMatch[2]
    if (alphaHex && alphaHex.toLowerCase() !== 'ff') {
      const r = Number.parseInt(rgb.slice(0, 2), 16)
      const g = Number.parseInt(rgb.slice(2, 4), 16)
      const b = Number.parseInt(rgb.slice(4, 6), 16)
      const a = Number.parseInt(alphaHex, 16) / 255
      return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(3))})`
    }
    return `#${rgb}`
  }

  if (/^#?[0-9a-fA-F]{3,8}$/.test(s)) return s.startsWith('#') ? s : `#${s}`
  return s
}

/** FontStyle: число (биты AWT Font) или строка PLAIN/BOLD/ITALIC. */
export function swingFontStyleToCss(raw: unknown): { fontWeight?: string; fontStyle?: string } {
  const out: { fontWeight?: string; fontStyle?: string } = {}
  if (raw === null || raw === undefined || raw === '') return out

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = raw >>> 0
    if (n & 1) out.fontWeight = 'bold'
    if (n & 2) out.fontStyle = 'italic'
    return out
  }

  const str = String(raw).trim()
  if (!str) return out

  const asInt = Number.parseInt(str, 10)
  if (String(asInt) === str && asInt >= 0 && asInt <= 3) {
    if (asInt & 1) out.fontWeight = 'bold'
    if (asInt & 2) out.fontStyle = 'italic'
    return out
  }

  const u = str.toUpperCase()
  if (u === 'PLAIN' || u === '0') return out
  if (u.includes('BOLD')) out.fontWeight = 'bold'
  if (u.includes('ITALIC')) out.fontStyle = 'italic'
  return out
}

/** Отступы ячейки: если уже CSS — как есть; иначе числа через пробел/запятую. */
export function swingPaddingToCss(raw: string): string {
  const t = raw.trim()
  if (!t) return ''
  if (/px|%|rem|em|pt|ex|ch|cm|mm|in|vh|vw/i.test(t)) return t
  const nums = t
    .split(/[\s;,]+/)
    .map(Number)
    .filter((n) => Number.isFinite(n))
  if (nums.length === 1) return `${nums[0]}px`
  if (nums.length === 2) return `${nums[0]}px ${nums[1]}px`
  if (nums.length === 3) return `${nums[0]}px ${nums[1]}px ${nums[2]}px`
  if (nums.length >= 4) return `${nums[0]}px ${nums[1]}px ${nums[2]}px ${nums[3]}px`
  return t
}

function mergeColumnCell(
  perColumn: Record<string, TreeTableColumnPresentation>,
  col: string,
  patch: Partial<TreeTableColumnPresentation>,
): void {
  if (!col) return
  const prev = perColumn[col] ?? {}
  perColumn[col] = { ...prev, ...patch }
}

/**
 * @param row — плоские данные строки (node.data)
 * @param columnIds — значения column.value в порядке таблицы (для ignoreReadOnly ключей)
 */
export function buildTreeTableRowSwingPresentation(
  row: unknown,
  columnIds: string[],
): TreeTableRowSwingPresentation {
  const r = asRecord(row)
  const perColumn: Record<string, TreeTableColumnPresentation> = {}
  const rowInlineStyle: Record<string, string> = {}

  const rowBg = readSwingRowString(r, 'row__color')
  if (rowBg) rowInlineStyle.backgroundColor = normalizeCssColor(rowBg)

  const rowFg = readSwingRowString(r, 'rowForeground')
  if (rowFg) rowInlineStyle.color = normalizeCssColor(rowFg)

  const rowFontPx = readSwingRowString(r, 'rowFontSize')
  if (rowFontPx) {
    const n = Number.parseFloat(rowFontPx)
    rowInlineStyle.fontSize = Number.isFinite(n) ? `${n}px` : rowFontPx
  }

  const rowFontStyleRaw = readSwingRowKey(r, 'FontStyle')
  Object.assign(rowInlineStyle, swingFontStyleToCss(rowFontStyleRaw))

  const rowTitle = readSwingRowString(r, 'row__tooltip')

  let iconFileName = readSwingRowString(r, 'image')
  if (!iconFileName) iconFileName = readSwingRowString(r, '__image')

  const cellColorRaw = readSwingRowString(r, 'cellColor')
  for (const { column, value } of parseSwingKeyedComposite(cellColorRaw)) {
    if (!columnIds.includes(column)) continue
    mergeColumnCell(perColumn, column, { color: normalizeCssColor(value) })
  }

  const cellBgRaw = readSwingRowString(r, 'cellBgColor')
  for (const { column, value } of parseSwingKeyedComposite(cellBgRaw)) {
    if (!columnIds.includes(column)) continue
    mergeColumnCell(perColumn, column, { backgroundColor: normalizeCssColor(value) })
  }

  const cellFontRaw = readSwingRowString(r, 'cellFontStyle')
  for (const { column, value } of parseSwingKeyedComposite(cellFontRaw)) {
    if (!columnIds.includes(column)) continue
    mergeColumnCell(perColumn, column, swingFontStyleToCss(value))
  }

  const cellPadRaw = readSwingRowString(r, 'cellPadding')
  for (const { column, value } of parseSwingKeyedComposite(cellPadRaw)) {
    if (!columnIds.includes(column)) continue
    mergeColumnCell(perColumn, column, { padding: swingPaddingToCss(value) })
  }

  for (const col of columnIds) {
    const iroKey = `${col}__ignoreReadOnly`
    const raw = readSwingRowKey(r, iroKey)
    if (raw === undefined) continue
    const u = unwrapField(raw)
    const truthy =
      u === true ||
      u === 'true' ||
      u === 1 ||
      u === '1' ||
      String(u).toLowerCase() === 'yes'
    mergeColumnCell(perColumn, col, { ignoreReadOnly: truthy })
  }

  return {
    rowInlineStyle,
    rowTitle,
    iconFileName: iconFileName || undefined,
    perColumn,
  }
}
